import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { AuthLayout } from "@/components/auth-layout";
import { FormField } from "@/components/form-field";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ApiRequestError } from "@/lib/api";
import { queries } from "@/lib/queries";
import { formatDate } from "@/lib/utils";

const searchSchema = z.object({ codigo: z.string().optional() });

/** Página pública exigida pelo app Meta: status do pedido de exclusão de dados. */
export const Route = createFileRoute("/exclusao-de-dados")({
  validateSearch: searchSchema,
  component: ExclusaoDeDadosPage,
});

function ExclusaoDeDadosPage() {
  const { codigo } = Route.useSearch();
  const navigate = useNavigate();
  const [input, setInput] = useState(codigo ?? "");
  const status = useQuery(queries.dataDeletionStatus(codigo ?? ""));

  return (
    <AuthLayout
      title="Exclusão de dados"
      description="Acompanhe o pedido de exclusão dos seus dados da Meta no IAGENIX Ads."
    >
      <div className="space-y-6">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void navigate({
              to: "/exclusao-de-dados",
              search: { codigo: input.trim().toUpperCase() },
            });
          }}
        >
          <FormField
            id="codigo"
            label="Código de confirmação"
            hint="Você recebeu este código da Meta ao remover o IAGENIX Ads."
          >
            <Input
              id="codigo"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ex.: A7K2M9P4Q8R3"
              autoComplete="off"
            />
          </FormField>
          <Button type="submit" className="w-full" disabled={input.trim().length === 0}>
            Consultar
          </Button>
        </form>

        {codigo ? (
          status.isPending ? (
            <p className="text-sm text-muted-foreground" role="status">
              Consultando…
            </p>
          ) : status.isError ? (
            <Alert variant="destructive">
              <AlertTitle>Pedido não encontrado</AlertTitle>
              <AlertDescription>
                {status.error instanceof ApiRequestError && status.error.status === 404
                  ? "Não encontramos um pedido com este código. Confira se digitou certo."
                  : "Não foi possível consultar agora. Tente de novo em instantes."}
              </AlertDescription>
            </Alert>
          ) : (
            <Alert>
              <AlertTitle>
                {status.data.status === "concluida" ? "Exclusão concluída" : "Pedido recebido"}
              </AlertTitle>
              <AlertDescription>
                Código <strong>{status.data.confirmationCode}</strong>. Recebido em{" "}
                {formatDate(status.data.receivedAt)}
                {status.data.completedAt
                  ? ` e concluído em ${formatDate(status.data.completedAt)}`
                  : ""}
                .
                {status.data.status === "concluida"
                  ? " Apagamos as conexões, os tokens e todos os ativos importados da sua conta Meta."
                  : " Estamos processando a exclusão."}
              </AlertDescription>
            </Alert>
          )
        ) : null}

        <div className="space-y-2 text-sm text-muted-foreground">
          <p className="font-medium text-foreground">Como pedir a exclusão</p>
          <p>
            Nas configurações do Facebook, abra <em>Apps e sites</em>, encontre o IAGENIX Ads e
            remova-o. A Meta nos avisa na hora e apagamos tudo que importamos da sua conta:
            conexões, tokens, Business Managers, contas de anúncio, páginas e pixels. Você recebe um
            código para acompanhar aqui.
          </p>
          <p>
            Para apagar também a sua conta no IAGENIX Ads, escreva para{" "}
            <a className="underline" href="mailto:iagenixcloud@gmail.com">
              iagenixcloud@gmail.com
            </a>
            .
          </p>
        </div>
      </div>
    </AuthLayout>
  );
}
